/**
 * overflowTitle.js — единый стандарт: текст не влез → полный текст во всплывающей
 * подсказке по наведению. Для ВСЕГО интерфейса, без правок по месту.
 *
 * Покрытие:
 *   1. input (текстовые и number) и textarea — по scrollWidth/scrollHeight;
 *   2. select — выбранный пункт меряем canvas-ом: у select scrollWidth обрезку не
 *      отражает;
 *   3. любой элемент с text-overflow: ellipsis или line-clamp (названия в списках,
 *      чипы, ячейки) — ищем среди цели наведения и её 3 ближайших предков.
 *
 * Почему глобальный делегат, а не `title={value}` в каждом месте.
 *  - Стандарт «для всех» нельзя обеспечить дисциплиной: забытое поле снова
 *    молча режет текст.
 *  - `title={value}` показывает подсказку и на коротком тексте. Здесь она
 *    появляется ТОЛЬКО при реальной обрезке и пропадает, когда текст влезает.
 *
 * Правила:
 *  - Свой `title` разработчика (пояснение «Кол-во», «Единица») не теряем. Если у
 *    поля ввода/select он есть и текст обрезан — подсказка «пояснение + перевод
 *    строки + полный текст». У прочих элементов свой title уже даёт полный текст
 *    (title={r.name}) — их не трогаем.
 *  - Наш title отслеживаем в WeakMap: если React перезаписал атрибут своим
 *    значением, это снова «свой» title, а не наш.
 *  - Обновляем и на `input`/`change`: пока курсор над полем, текст меняется.
 *  - Ни состояния, ни ререндеров: один атрибут DOM на наведение.
 *  - Отключённое поле (замороженная смета) нужнее всего — раскрыть его кликом
 *    нельзя. Часть браузеров не шлёт mouseover на disabled-контрол, поэтому
 *    слушаем и pointerover.
 *  - Тач-экраны: hover-а нет, нативный title не показывается. Механизм безвреден,
 *    но пользы там не даёт — для телефона нужно отдельное решение (тап).
 */
const TEXT_TYPES = new Set(['text', 'search', 'email', 'tel', 'url', 'number', '']);
const ANCESTOR_DEPTH = 3;
const ours = new WeakMap(); // el -> последний title, выставленный нами

let measureCtx = null;
function textWidth(text, cs) {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  return measureCtx.measureText(text).width;
}

// «Свой» title элемента: то, что стоит в атрибуте и поставлено не нами.
function ownTitle(el) {
  const cur = el.getAttribute('title');
  if (cur == null) return '';
  return ours.has(el) && ours.get(el) === cur ? (el.dataset.otBase || '') : cur;
}

function apply(el, fullText, composeWithOwn) {
  const base = composeWithOwn ? ownTitle(el) : '';
  // Свой title уже может нести ТОТ ЖЕ полный текст (title={l.comment} у поля
  // примечания сметы) — тогда добавлять его второй раз нельзя: в подсказке
  // текст шёл бы дважды подряд.
  const next = !base ? fullText : (base.includes(fullText) ? base : `${base}\n${fullText}`);
  if (el.getAttribute('title') !== next) el.setAttribute('title', next);
  el.dataset.otBase = base;
  ours.set(el, next);
}

function clear(el) {
  if (!ours.has(el)) return;
  const base = el.dataset.otBase || '';
  if (base) el.setAttribute('title', base); else el.removeAttribute('title');
  delete el.dataset.otBase;
  ours.delete(el);
}

function inputOverflows(el) {
  // +1: браузеры округляют субпиксели и дают ложное «1px переполнения».
  return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
}

function syncField(el) {
  const text = el.value || '';
  if (text && inputOverflows(el)) apply(el, text, true); else clear(el);
}

function syncSelect(el) {
  const opt = el.options && el.options[el.selectedIndex];
  const text = opt ? (opt.text || '').trim() : '';
  if (!text) { clear(el); return; }
  const cs = getComputedStyle(el);
  // Нативная стрелка не входит в padding, кастомная (appearance: none) — входит.
  const arrow = cs.appearance === 'none' || cs.webkitAppearance === 'none' ? 0 : 20;
  const room = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - arrow;
  if (textWidth(text, cs) > room + 1) apply(el, text, true); else clear(el);
}

function clipped(el) {
  const cs = getComputedStyle(el);
  if (cs.textOverflow === 'ellipsis' && el.scrollWidth > el.clientWidth + 1) return true;
  const clamp = cs.webkitLineClamp || cs.lineClamp;
  return !!clamp && clamp !== 'none' && el.scrollHeight > el.clientHeight + 1;
}

function syncClipped(start) {
  let el = start;
  for (let i = 0; el && el.nodeType === 1 && i <= ANCESTOR_DEPTH; i += 1, el = el.parentElement) {
    if (el === document.body) return;
    // Свой title элемента — разработчик уже решил, что показывать.
    if (el.hasAttribute('title') && !ours.has(el)) continue;
    if (clipped(el)) {
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) apply(el, text, false);
      return;
    }
    clear(el);
  }
}

function sync(target) {
  if (!target || target.nodeType !== 1) return;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || (tag === 'INPUT' && TEXT_TYPES.has((target.getAttribute('type') || '').toLowerCase()))) {
    syncField(target);
  } else if (tag === 'SELECT') {
    syncSelect(target);
  } else if (tag !== 'INPUT' && tag !== 'OPTION') {
    syncClipped(target);
  }
}

let started = false;
export function initOverflowTitle() {
  if (started || typeof document === 'undefined') return;
  started = true;
  const onEvt = (e) => sync(e.target);
  // capture: событие поля внутри модала с остановкой всплытия иначе не дойдёт.
  ['mouseover', 'pointerover', 'focusin', 'input', 'change'].forEach(n => document.addEventListener(n, onEvt, true));
}

// Для тестов: прогнать синхронизацию без событий.
export const __sync = sync;
